/*
 * Topics Module
 */

define([
  "marionette",
  "messaging/messenger",
  "backbone",
  "./topicsView",
  "modules/shared/navTree/navTreeModule",
  "./topicsList/topicsListModule",
  "./standardResponsesList/standardResponsesListModule",
  "./topicSearch/topicSearchModule",
  "./variablePromptsList/variablePromptsListModule",
  "./snippetList/snippetListModule",
  "../snippet/snippetModel",
], function (
  Marionette,
  Messenger,
  Backbone,
  TopicsView,
  NavTreeModule,
  TopicsListModule,
  StandardResponsesListModule,
  TopicSearchModule,
  VariablePromptsModule,
  SnippetListModule,
  SnippetModel,
) {
  const TABS = {
    ACTIVE: 1,
    SEARCH: 2,
    ARCHIVED: 3,
  };

  const LIST_TYPES = {
    SNIPPETS: 1,
    STANDARD_RESPONSES: 2,
    VARIABLE_PROMPTS: 3,
  };

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "topics";
      this.region = options.region;
      this.rootTopicGroup = options.rootTopicGroup;
      this.rootSnippetGroup = options.rootSnippetGroup;
      this.selectedTopicGroup = null; //selected group from the navtree
      this.selectedArchivedTopicGroup = null; //selected group from the navtree for archived topics
      this.selectedSnippetGroup = null;
      this.activeTab = TABS.ACTIVE;
      this._setUpModelListeners();
      this._setUpVents();
    },
    reloadData: function (expandSnippetTree) {
      switch (this.activeTab) {
        case TABS.ACTIVE:
          this._showActiveTopics(expandSnippetTree);
          break;
        case TABS.ARCHIVED:
          this._showArchivedTopics();
          break;
        case TABS.SEARCH:
          // Do not do anything for the search tab, otherwise the last search results will be lost
          break;
      }
    },
    save: function () {
      if (this.navList.isOpen) {
        console.log("saving topics");
        if (this.rootTopicGroup) {
          this.rootTopicGroup.saveModel();
        } else {
          Messenger.saveAttempted(true); //success
        }
      } else if (this.snippetList.isOpen) {
        console.log("saving snippets");
        if (this.rootSnippetGroup) {
          this.rootSnippetGroup.saveModel();
        } else {
          Messenger.saveAttempted(true); //success
        }
      } else if (this.standardResponsesList.isOpen) {
        console.log("saving standard responses");
        this.standardResponses.saveCollection();
      } else {
        //nothing is open yet, so nothing to save
        Messenger.saveAttempted(true);
      }
    },
    show: function (expandSnippetTree) {
      const showArchivedTopics = Messenger.reqres.request("default:showArchivedTopics");
      const model = new Backbone.Model({
        showArchivedTopics: showArchivedTopics,
        showButtons: true,
        showButtonsForArchived: false,
      });
      //toggle the variable prompts feature on/off in the template
      this.layout = new TopicsView.topicsLayout({ model: model });

      this.navTree = this.addSubModule(
        new NavTreeModule({
          region: this.layout.topicsTree,
          nameAttribute: "name",
          namespace: "topics",
        }),
      );

      this.snippetsNavTree = this.addSubModule(
        new NavTreeModule({
          region: this.layout.snippetsTree,
          nameAttribute: "name",
          namespace: "snippets",
        }),
      );

      if (showArchivedTopics) {
        const navBtns = Messenger.reqres.request("navigationButtons:archived");
        this.archivedTopicsList = this.addSubModule(
          new TopicsListModule({
            region: this.layout.topicsList,
            rootTopicGroup: this.rootTopicGroup,
            namespace: "archivedTopicsList",
          }),
          navBtns,
        );
      }
      const navBtns = Messenger.reqres.request("navigationButtons:search");
      this.navList = this.addSubModule(
        new TopicsListModule({
          region: this.layout.topicsList,
          rootTopicGroup: this.rootTopicGroup,
          namespace: "activeTopicsList",
        }),
        navBtns,
      );
      this.snippetList = this.addSubModule(
        new SnippetListModule({
          region: this.layout.topicsList,
          rootSnippetGroup: this.rootSnippetGroup,
        }),
      );
      this.standardResponsesList = this.addSubModule(
        new StandardResponsesListModule({ region: this.layout.topicsList }),
      );
      this.variablePromptsList = this.addSubModule(
        new VariablePromptsModule({ region: this.layout.topicsList }),
      );
      this.search = this.addSubModule(new TopicSearchModule({ region: this.layout.searchPane }));

      this.layout.on(
        "add:topic",
        function () {
          this._addTopic();
        },
        this,
      );

      //dropped learner transcript onto the plus sign, so create a new topic and apply the transcript
      this.layout.on(
        "dropped:transcript",
        function () {
          //get the dragged model
          const learnerTranscript = Messenger.reqres.request("dragged:model");
          let localeId;
          if (learnerTranscript.get("localeId")) {
            localeId = learnerTranscript.get("localeId");
          } else if (
            learnerTranscript.get("children") &&
            learnerTranscript.get("children").length > 0
          ) {
            localeId = learnerTranscript.get("children")[0].representative.localeId;
          }
          if (!localeId) {
            localeId = Messenger.reqres.request("default:interface:language");
          }
          this._addTopic(learnerTranscript, localeId);
        },
        this,
      );

      this.layout.on("show:snippets", () => {
        this._saveAndSwitchList(LIST_TYPES.SNIPPETS);
      });

      this.layout.on("show:standard:responses", () => {
        this._saveAndSwitchList(LIST_TYPES.STANDARD_RESPONSES);
      });

      this.layout.on("show:variable:prompts", () => {
        this._saveAndSwitchList(LIST_TYPES.VARIABLE_PROMPTS);
      });

      this.layout.on("show:archived:topics", this._showArchivedTopics, this);
      this.layout.on("show:navigation:topics", this._showActiveTopics, this);
      this.layout.on("show:search:topics", this._showSearchTopics, this);
      this.layout.on("show", () => this._showSubViews(expandSnippetTree));
      this.showRegion(this.layout);
    },
    _saveAndSwitchList: function (listType) {
      //save before switching views
      Messenger.save((success) => {
        if (success) {
          this.selectedTopicGroup = null;
          this.selectedSnippetGroup = null;
          this.selectedArchivedTopicGroup = null;
          Messenger.vent.trigger("topics:clear:selection"); // deselect whatever the nav tree had already selected, if anything
          Messenger.vent.trigger("snippets:clear:selection");
          this.layout.unselectStandardResponse();
          this.layout.unselectSnippets();
          this.layout.unselectVariablePrompts();

          switch (listType) {
            case LIST_TYPES.SNIPPETS:
              this.layout.selectSnippets();
              this.layout.collapseTopicsTreeAndShowSnippetsTree();
              // toggle collapse topics tree if it is expanded
              this.navTree.collapse();
              this.rootSnippetGroup.trigger("showSelect");
              this.snippetList.show(this.rootSnippetGroup);
              break;

            case LIST_TYPES.VARIABLE_PROMPTS:
              if (!this.variablePrompts) {
                this.variablePrompts = Messenger.reqres.request("conversation:variables");
              }
              this.layout.selectVariablePrompts();
              this.variablePromptsList.show(this.variablePrompts);
              break;

            case LIST_TYPES.STANDARD_RESPONSES:
              if (!this.standardResponses) {
                this.standardResponses = Messenger.reqres.request("standard:responses");
              }
              this.layout.selectStandardResponse();
              this.standardResponsesList.show(this.standardResponses);
              break;
          }
        }
      });
    },
    //add a new topic to the selected folder (or root if none has been selected)
    //if the optional transcriptToApply is passed, then fire a vent to apply the transcript after the topic page is loaded
    _addTopic: function (transcriptToApply, localeId) {
      //make sure topics page is saved before creating new topic
      Messenger.save(function (success) {
        if (success) {
          let newTopic;
          //if a topic group has been selected, add the topic to that group, otherwise, add it to the root group
          if (this.activeTab === TABS.ARCHIVED) {
            if (
              this.archivedTopicsList &&
              this.archivedTopicsList.group &&
              this.archivedTopicsList.group.get("groupId") >= 0
            ) {
              newTopic = this.archivedTopicsList.group.addTopic();
            } else {
              newTopic = this.rootTopicGroup.addTopic();
            }
          } else {
            if (
              this.standardResponsesList &&
              !this.standardResponsesList.isOpen &&
              this.navList &&
              this.navList.group &&
              this.navList.group.get("groupId") >= 0
            ) {
              //make sure we aren't looking at search results
              newTopic = this.navList.group.addTopic();
            } else {
              newTopic = this.rootTopicGroup.addTopic();
            }
          }

          //show the topic page for the new topic
          Messenger.vent.trigger("show:topic", newTopic);
          if (transcriptToApply) {
            //fire an event for the topic module to handle applying the transcript to the topic
            Messenger.vent.trigger("apply:transcript:to:topic", transcriptToApply, localeId);
          }
        }
      }, this);
    },
    _showSubViews: function (expandSnippetTree) {
      if (this.selectedTopicGroup) {
        this.selectedTopicGroup.refreshTopicCollectionAndClearChildTopicCollections();
      } else if (this.selectedArchivedTopicGroup) {
        this.selectedArchivedTopicGroup.refreshArchivedTopicCollectionAndClearChildArchivedTopicCollections();
      } else {
        this.rootTopicGroup.refreshTopicCollectionAndClearChildTopicCollections();
        this.rootTopicGroup.refreshArchivedTopicCollectionAndClearChildArchivedTopicCollections();
      }
      const doNotExpandTopicTree = expandSnippetTree;
      this.navTree.show(this.rootTopicGroup, doNotExpandTopicTree); //pass root topic folder object
      this.snippetsNavTree.show(this.rootSnippetGroup, !doNotExpandTopicTree);
      this.search.show();
      if (expandSnippetTree) {
        if (this.selectedSnippetGroup) {
          this.selectedSnippetGroup.onSynced(() => {
            this.selectedSnippetGroup.trigger("select");
          });
        } else {
          this.rootSnippetGroup.onSynced(() => {
            this.rootSnippetGroup.trigger("select");
          });
        }
      } else {
        if (this.selectedTopicGroup) {
          this.selectedTopicGroup.onSynced(() => {
            this.selectedTopicGroup.trigger("select");
          });
        } else if (this.selectedArchivedTopicGroup) {
          this.selectedArchivedTopicGroup.onSynced(() => {
            this.selectedArchivedTopicGroup.trigger("select");
          });
        } else {
          this.rootTopicGroup.onSynced(() => {
            this.rootTopicGroup.trigger("select");
          });
        }
      }
    },
    _showActiveTopics: function (expandSnippetTree) {
      this.activeTab = TABS.ACTIVE;
      if (expandSnippetTree) {
        this.navTree.collapse();
        this.layout.collapseTopicsTreeAndShowSnippetsTree();
        if (this.selectedSnippetGroup) {
          this.selectedSnippetGroup.trigger("showSelect");
        } else {
          this.snippetsNavTree.show(this.rootSnippetGroup);
          this.rootSnippetGroup.trigger("showSelect");
        }
      } else {
        this.snippetsNavTree.collapse();
        this.layout.expandTopicsTreeAndCollapseSnippetsTree();
        if (this.selectedTopicGroup) {
          this.selectedTopicGroup.refreshTopicCollectionAndClearChildTopicCollections();
          this.selectedTopicGroup.trigger("showSelect");
        } else {
          this.rootTopicGroup.refreshTopicCollectionAndClearChildTopicCollections();
          this.navTree.show(this.rootTopicGroup);
          this.rootTopicGroup.trigger("showSelect");
        }
      }
    },
    _showArchivedTopics: function () {
      this.activeTab = TABS.ARCHIVED;
      //Checking if previously some sub-folder is selected than clean only that folder
      //Else clean entire list
      if (this.selectedArchivedTopicGroup) {
        this.selectedArchivedTopicGroup.refreshArchivedTopicCollectionAndClearChildArchivedTopicCollections();
        this.selectedArchivedTopicGroup.trigger("showSelect");
      } else {
        this.rootTopicGroup.refreshArchivedTopicCollectionAndClearChildArchivedTopicCollections();
        this.navTree.show(this.rootTopicGroup);
        this.rootTopicGroup.trigger("showSelect");
      }
    },
    _showArchivedTopicsList: function (groupView) {
      const navBtns = Messenger.reqres.request("navigationButtons:archived");
      this._showTopicsListCommon(groupView, this.selectedArchivedTopicGroup, navBtns);
    },
    _showSearchTopics: function () {
      this.activeTab = TABS.SEARCH;
    },
    _showTopicsList: function (groupView) {
      this._showTopicsListCommon(groupView, this.selectedTopicGroup);
    },
    _showTopicsListCommon: function (groupView, selectedGroup) {
      //check if we can close and save any open textboxes
      if (Messenger.reqres.request("save:textbox")) {
        //make sure that the current selected group is safe to save (eventually)
        if (!selectedGroup || selectedGroup.isValid()) {
          if (groupView.model !== selectedGroup) {
            selectedGroup = groupView.model;
            if (selectedGroup.get("groupId") > 0) {
              if (this.activeTab === TABS.ARCHIVED) {
                this.selectedArchivedTopicGroup = selectedGroup;
              } else {
                this.selectedTopicGroup = selectedGroup;
              }
            } else {
              if (this.activeTab === TABS.ARCHIVED) {
                this.selectedArchivedTopicGroup = null;
              } else {
                this.selectedTopicGroup = null;
              }
            }
          }
          this.layout.unselectStandardResponse();
          this.layout.unselectVariablePrompts();
          this.layout.unselectSnippets();
          if (this.activeTab === TABS.ARCHIVED) {
            const navBtns = Messenger.reqres.request("navigationButtons:archived");
            this.archivedTopicsList.show(groupView.model, navBtns);
          } else {
            const navBtns = Messenger.reqres.request("navigationButtons:search");
            this.navList.show(groupView.model, navBtns);
          }
          return;
        }
        selectedGroup.trigger("showSelect");
      }
    },
    _showSnippetsList: function (groupView, selectedGroup) {
      //check if we can close and save any open textboxes
      if (Messenger.reqres.request("save:textbox")) {
        //make sure that the current selected group is safe to save (eventually)
        if (!selectedGroup || selectedGroup.isValid()) {
          if (groupView.model !== selectedGroup) {
            selectedGroup = groupView.model;
            if (selectedGroup.get("groupId") > 0) {
              this.selectedSnippetGroup = selectedGroup;
            } else {
              this.selectedSnippetGroup = null;
            }
          }
          this.layout.unselectStandardResponse();
          this.layout.unselectVariablePrompts();
          this.snippetList.show(groupView.model);
          return;
        }
        selectedGroup.trigger("showSelect");
      }
    },
    _setUpVents: function () {
      //select a folder from the topics nav tree
      Messenger.vent.on(
        "topics:select:group",
        (groupView) => {
          Messenger.vent.trigger("snippets:clear:selection");
          this._showTopicsList(groupView);
        },
        this,
      );

      //select a folder from the snippets nav tree
      Messenger.vent.on(
        "snippets:select:group",
        (groupView) => {
          Messenger.vent.trigger("topics:clear:selection");
          // save any pending changes anywhere in the snippet tree
          if (this.rootSnippetGroup) {
            this.rootSnippetGroup.saveModel();
          }
          this._showSnippetsList(groupView, this.selectedSnippetGroup);
        },
        this,
      );

      //show the results of a search
      Messenger.vent.on(
        "topics:search:results",
        function (groupModel, options) {
          this.selectedTopicGroup = null;
          this.selectedSnippetGroup = null;
          this.selectedArchivedTopicGroup = null;
          //since there could be modifications made to topics in the search, we reset the topics nav tree
          this.rootTopicGroup.clearTopicCollections();
          this.rootTopicGroup.clearArchivedTopicCollections();
          Messenger.vent.trigger("topics:clear:selection"); //deselect whatever the nav tree had already selected, if anything
          this.layout.unselectStandardResponse();
          this.layout.unselectVariablePrompts();
          this.layout.unselectSnippets();
          this.navList.show(groupModel, options);
        },
        this,
      );
      Messenger.vent.on(
        "archivedTopics:select:group",
        (groupView) => {
          this._showArchivedTopicsList(groupView);
        },
        this,
      );
      Messenger.vent.on(
        "add:topic",
        function () {
          //make sure that the root topic is available:
          this.rootTopicGroup.onSynced(function () {
            this._addTopic();
          }, this);
        },
        this,
      );
    },
    _setUpModelListeners: function () {
      // setup custom triggers just for the root folders, so that we can adjust the height of topics tree and snippets tree
      this.rootSnippetGroup.on("folder:expanded", () => {
        // toggle collapse topics tree if snippets tree is expanded
        this.navTree.collapse();
        this.layout.collapseTopicsTreeAndShowSnippetsTree();
      });
      this.rootTopicGroup.on("folder:expanded", () => {
        // toggle collapse snippets tree if topics tree is expanded
        this.snippetsNavTree.collapse();
        this.layout.expandTopicsTreeAndCollapseSnippetsTree();
      });
      this.rootSnippetGroup.on("folder:collapsed", () => {
        this.layout.collapseSnippetsTree();
      });
      this.rootTopicGroup.on("folder:collapsed", () => {
        this.layout.collapseTopicsTree();
      });
    },
  });
});
